import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { type PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@sine/auth-client';
import { type Team, useTeamSwitcher } from '@sine/sine-team-switcher';

import { CoreFeatures } from '../../services/features/feature.constants';
import { RoleType } from '../../services/permission/permission.types';
import { useMyTeams } from '../../services/team/team.hooks';
import { useEnabledFeatures } from '../hooks';
import { getPages } from '../pages/pages.helper';
import type { Page } from '../pages/pages.types';

interface PermissionsContext {
  isLobbyModeEnabled: boolean;
  isLoading: boolean;
  currentTeam?: Team;
  totalTeamCount: number;
  isCurrentTeamAdmin: boolean;
  enabledFeatures: Array<string>;
  visiblePages: Array<Page>;
}

const defaultPermissionsContext: PermissionsContext = {
  isLobbyModeEnabled: false,
  isLoading: false,
  currentTeam: undefined,
  totalTeamCount: 0,
  isCurrentTeamAdmin: false,
  enabledFeatures: [],
  visiblePages: [],
};

const PermissionsContext = createContext(defaultPermissionsContext);

type PermissionsProviderProps = PropsWithChildren<unknown>;

export default function PermissionsProvider({ children }: PermissionsProviderProps) {
  const navigate = useNavigate();
  const { isImpersonating } = useAuth();
  const flags = useFlags();
  const { currentTeam, teamList, isLoading: isLoadingCurrentTeam } = useTeamSwitcher();
  const { combinedFeatures: enabledFeatures, isLoading: isLoadingEnabledFeatures } = useEnabledFeatures();
  const [pages, setPages] = useState<Array<Page>>([]);

  const { data: myTeams = [], isLoading: isLoadingTeams } = useMyTeams();

  const isLobbyModeEnabled = enabledFeatures.includes(CoreFeatures.LOBBY_MODE);
  const tmaTeamMatch = myTeams.find((team) => team.id === currentTeam?.id);
  const isCurrentTeamAdmin = [RoleType.TeamOwner, RoleType.TeamAdmin].some((permission) =>
    tmaTeamMatch?.roles.includes(permission),
  );

  useEffect(() => {
    setPages(getPages(navigate, isImpersonating, isCurrentTeamAdmin, enabledFeatures, flags));
  }, [enabledFeatures, navigate, isImpersonating, isCurrentTeamAdmin, flags]);

  const value = useMemo<PermissionsContext>(
    () => ({
      isLobbyModeEnabled,
      isLoading: isLoadingCurrentTeam || isLoadingTeams || isLoadingEnabledFeatures,
      currentTeam,
      totalTeamCount: teamList.length ?? 0,
      isCurrentTeamAdmin,
      enabledFeatures,
      visiblePages: pages.filter((page) => page.hasPermission),
    }),
    [
      isLobbyModeEnabled,
      isLoadingCurrentTeam,
      isLoadingTeams,
      isLoadingEnabledFeatures,
      currentTeam,
      teamList,
      enabledFeatures,
      isCurrentTeamAdmin,
      pages,
    ],
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export function usePermissions() {
  return useContext(PermissionsContext);
}
